import React, { useState, useMemo } from "react";
import { useGetAllTicketsQuery } from "@/features/api/supportApi";
import {
  Card,
  CardContent,
  CardHeader,
  CardTitle,
} from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import {
  LoaderCircle,
  CheckCircle2,
  Clock,
  Plus,
  MessageCircle,
  Bell,
} from "lucide-react";
import { useSelector } from "react-redux";
import { Navigate, useNavigate } from "react-router-dom";
import SupportTicketForm from "./SupportTicketForm";

/* ================= STATUS CONFIG ================= */
const statusConfig = {
  pending: {
    label: "Pending",
    color: "bg-yellow-500",
    icon: <Clock className="w-4 h-4" />,
  },
  processing: {
    label: "In Progress",
    color: "bg-blue-500",
    icon: <LoaderCircle className="w-4 h-4 animate-spin" />,
  },
  replied: {
    label: "Replied",
    color: "bg-green-600",
    icon: <CheckCircle2 className="w-4 h-4" />,
  },
};

const normalizeStatus = (status) =>
  status ? status.toLowerCase() : "pending";

/* ================= UNREAD CHECK ================= */
const hasUnreadReply = (ticket) => {
  if (!ticket.replies || ticket.replies.length === 0) return false;

  const lastReplyAt =
    ticket.replies[ticket.replies.length - 1]?.repliedAt;

  const lastRead = localStorage.getItem(
    `ticket_read_${ticket._id}`
  );

  if (!lastRead) return true;

  return new Date(lastReplyAt) > new Date(lastRead);
};

const UserSupportTickets = () => {
  const { user, isAuthenticated } = useSelector((state) => state.auth);
  const { data, isLoading, error } = useGetAllTicketsQuery();
  const [showForm, setShowForm] = useState(false);
  const navigate = useNavigate();

  if (!isAuthenticated || !user) {
    return <Navigate to="/login" replace />;
  }

  const tickets = Array.isArray(data) ? data : [];

  /* ================= FILTER USER TICKETS ================= */
  const userTickets = useMemo(
    () => tickets.filter((t) => t.email === user.email),
    [tickets, user.email]
  );

  /* ================= OPEN CHAT ================= */
  const openChat = (ticketId) => {
    localStorage.setItem(
      `ticket_read_${ticketId}`,
      new Date().toISOString()
    );
    navigate(`/dashboard/support/${ticketId}`);
  };

  return (
    <div className="flex justify-center px-4 py-10">
      <div className="w-full max-w-4xl space-y-6">
        {/* Header */}
        <div className="flex items-center justify-between">
          <h2 className="text-2xl font-bold">My Support Tickets</h2>
          <Button
            variant="outline"
            className="flex gap-2"
            onClick={() => setShowForm((prev) => !prev)}
          >
            <Plus size={16} />
            {showForm ? "Close" : "New Ticket"}
          </Button>
        </div>

        {/* New Ticket */}
        {showForm && <SupportTicketForm />}

        {/* Loading */}
        {isLoading && (
          <p className="text-center text-gray-500">Loading tickets...</p>
        )}

        {/* Error */}
        {error && (
          <p className="text-center text-red-500">
            Failed to load tickets.
          </p>
        )}

        {/* Empty */}
        {!isLoading && userTickets.length === 0 && !showForm && (
          <p className="text-center text-gray-600 mt-6">
            You haven’t submitted any support tickets yet.
          </p>
        )}

        {/* Ticket List */}
        <div className="space-y-4">
          {userTickets.map((ticket) => {
            const statusKey = normalizeStatus(ticket.status);
            const status =
              statusConfig[statusKey] || statusConfig.pending;

            const unread = hasUnreadReply(ticket);

            return (
              <Card
                key={ticket._id}
                className="shadow-sm border rounded-md"
              >
                <CardHeader className="flex flex-row justify-between items-center">
                  <CardTitle className="text-lg">
                    {ticket.subject}
                  </CardTitle>

                  <div className="flex items-center gap-3">
                    {unread && (
                      <Badge className="bg-red-500 text-white flex gap-1">
                        <Bell size={12} />
                        New
                      </Badge>
                    )}

                    <Badge
                      className={`flex items-center gap-1 ${status.color} text-white`}
                    >
                      {status.icon}
                      {status.label}
                    </Badge>
                  </div>
                </CardHeader>

                <CardContent className="space-y-3">
                  <p className="text-sm text-gray-600">
                    {ticket.message.slice(0, 120)}...
                  </p>

                  {/* Chat Button */}
                  <div className="flex justify-end">
                    <Button
                      size="sm"
                      variant="outline"
                      className="flex gap-2"
                      onClick={() => openChat(ticket._id)}
                    >
                      <MessageCircle size={16} />
                      Open Chat
                    </Button>
                  </div>
                </CardContent>
              </Card>
            );
          })}
        </div>
      </div>
    </div>
  );
};

export default UserSupportTickets;
